// src/components/ui/StageActionBar.tsx
// A sticky commit bar carrying a readiness statement next to the primary
// action — replaces a bare `<button>` at the end of a long scroll. See
// docs/HMS_V4_UI_MODERNIZATION_ASSESSMENT.md backlog item 7.
//
// Sticky to the *bottom* of the viewport (not the stage's own scroll
// container), same convention as VisitStepper's sticky-to-top — so the
// commit action is reachable regardless of how far into a long form the
// nurse has scrolled.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface StageActionBarProps {
  /** A short statement of what's left, e.g. "All required sections completed" — states readiness rather than leaving the user to infer it from a disabled button. */
  readiness: ReactNode;
  readinessTone?: 'success' | 'warn' | 'neutral';
  /** Primary action(s) — typically one Button. */
  children: ReactNode;
  className?: string;
}

const TONE_CLASS: Record<NonNullable<StageActionBarProps['readinessTone']>, string> = {
  success: 'text-success-ink',
  warn: 'text-warn-ink',
  neutral: 'text-ink-muted',
};

export function StageActionBar({ readiness, readinessTone = 'neutral', children, className }: StageActionBarProps) {
  return (
    <div className={cn('sticky bottom-0 -mx-4 border-t border-line bg-card/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:px-5', className)}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className={cn('text-[12.5px] font-medium', TONE_CLASS[readinessTone])}>{readiness}</div>
        <div className="flex shrink-0 items-center gap-2">{children}</div>
      </div>
    </div>
  );
}
